import { useEffect, useState } from 'react'
import { AlertCircleIcon, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../../../lib/api'
import type { ProspectRecord } from '../../../types/prospects'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

interface DeleteProspectDialogProps {
  open: boolean
  prospect: ProspectRecord | null
  isSubmitting: boolean
  error: ApiError | null
  onClose: () => void
  onSubmit: (payload: { reason: string; rejection_reason: string; rejection_notes?: string }) => Promise<void>
}

const REJECTION_REASONS = [
  'duplicate',
  'invalid_contact',
  'wrong_program',
  'client_withdrew',
  'error_entry',
  'other',
] as const

export function DeleteProspectDialog({
  open,
  prospect,
  isSubmitting,
  error,
  onClose,
  onSubmit,
}: DeleteProspectDialogProps) {
  const { t } = useTranslation()
  const [rejectionReason, setRejectionReason] = useState('')
  const [detailedReason, setDetailedReason] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!open) return
    setRejectionReason('')
    setDetailedReason('')
    setNotes('')
  }, [open, prospect])

  const showDetailedReason = rejectionReason === 'other'

  const handleSubmit = async () => {
    if (!rejectionReason) return
    await onSubmit({
      reason: detailedReason || t(`prospects.rejectionReasons.${rejectionReason}`),
      rejection_reason: rejectionReason,
      rejection_notes: notes || undefined,
    })
  }

  return (
    <Dialog open={open && prospect !== null} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
              <Trash2 className="size-5" aria-hidden />
            </div>
            <div>
              <DialogTitle>{t('prospects.dialogs.deleteTitle')}</DialogTitle>
              <DialogDescription>{t('prospects.dialogs.deleteDescription')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {prospect ? (
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="text-sm font-semibold text-foreground">{prospect.contact_name}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {prospect.program_name ?? t('prospects.program')} · {prospect.business_name ?? t('prospects.business')}
            </p>
          </div>
        ) : null}

        <form
          id="delete-prospect-form"
          className="space-y-4"
          onSubmit={async (event) => {
            event.preventDefault()
            await handleSubmit()
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="delete-prospect-reason">{t('prospects.rejectionModal.reasonLabel')}</Label>
            <Select value={rejectionReason} onValueChange={setRejectionReason}>
              <SelectTrigger id="delete-prospect-reason">
                <SelectValue placeholder={t('prospects.rejectionModal.reasonPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {REJECTION_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {t(`prospects.rejectionModal.reasons.${reason}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showDetailedReason && (
            <div className="space-y-2">
              <Label htmlFor="delete-prospect-detailed-reason">{t('prospects.rejectionModal.notesLabel')}</Label>
              <Textarea
                id="delete-prospect-detailed-reason"
                value={detailedReason}
                onChange={(event) => setDetailedReason(event.target.value)}
                placeholder={t('prospects.rejectionModal.notesPlaceholder')}
                rows={3}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="delete-prospect-notes">{t('prospects.dialogs.reason')}</Label>
            <Textarea
              id="delete-prospect-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={t('prospects.rejectionModal.notesPlaceholder')}
              rows={2}
            />
          </div>

          {error?.errors?.reason?.[0] ? (
            <p className="text-sm text-destructive">{error.errors.reason[0]}</p>
          ) : null}

          {error && !error.errors ? (
            <Alert variant="destructive">
              <AlertCircleIcon />
              <AlertTitle>{t('prospects.errors.deleteFailed')}</AlertTitle>
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          ) : null}
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isSubmitting}>
              {t('prospects.rejectionModal.cancel')}
            </Button>
          </DialogClose>
          <Button type="submit" form="delete-prospect-form" variant="destructive" disabled={!rejectionReason || isSubmitting}>
            {isSubmitting ? t('prospects.rejectionModal.submitting') : t('prospects.rejectionModal.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
